// src/store/sagas/menuItemsSaga.ts

import { call, put, takeEvery, all } from "redux-saga/effects";
import { request } from "../../api/request";
import ENDPOINTS from "../../api/endpoints";

import {
  fetchMenuItemsRequest,
  fetchMenuItemsSuccess,
  fetchMenuItemsFailure,
  setMealTimes,
  MenuItem,
  MealTimeInfo,
} from "../slices/menuItemsSlice";

import { shouldSkipApiCalls } from "../../utils/appConfig";
import staticMenuData from "../../data/menuData.json";

interface MenuItemApiResponse {
  id: string;
  restaurantId: string;
  name: string;
  price: { dining: number; online: number };
  description: string | null;
  categoryName: string | null;
  inventoryStatus: boolean;
  isItemVerified: boolean;
  isDeleted: boolean;
  imageUrl: string | null;
  modifiers?: string[];
  modifierGroups?: any[];
  [key: string]: any;
}

interface MealDaySchedule {
  day: string;
  startsAt: string;
  endsAt: string;
}

interface ExtendedMealTime {
  id: string;
  name: string;
  description?: string;
  type?: string;
  startsAt: string | null;
  endsAt: string | null;
  days?: MealDaySchedule[];
  isActive?: boolean;
  settings?: {
    visibleOnWeb?: boolean;
    visibleOnApp?: boolean;
    [key: string]: any;
  };
  [key: string]: any;
}

/**
- Returns the current date/time converted strictly to America/New_York (EST/EDT)
- as a unified wall-clock UTC timestamp for safe comparison without local timezone shift.
 */
function getESTNow(mockDateInput?: string | Date): { currentWeekday: string; currentHour: number; currentMinute: number; estNowTimestamp: number } {
  const now = mockDateInput ? new Date(mockDateInput) : new Date();
  
  const estFormattedStr = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(now);

  const [datePart, timePart] = estFormattedStr.split(", ");
  const [month, day, year] = datePart.split("/").map(Number);
  const [currentHour, currentMinute, currentSecond] = timePart.split(":").map(Number);

  // Map EST components directly to a UTC epoch timestamp to represent pure wall-clock time
  const estNowTimestamp = Date.UTC(year, month - 1, day, currentHour, currentMinute, currentSecond);

  const currentWeekday = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    weekday: "long",
  }).format(now).toLowerCase();

  return { currentWeekday, currentHour, currentMinute, estNowTimestamp };
}

/**
- Parses an EST ISO string strictly as numerical components to avoid local timezone shifts.
 */
function parseEstWallClockTimestamp(dateStr: string): number {
  const [datePart, timePart = "00:00:00"] = dateStr.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const [h, min, s = "0"] = timePart.replace("Z", "").split(":").map(Number);
  
  return Date.UTC(y, m - 1, d, h, min, Number(s));
}

export function isCurrentMealTimeActive(meal: ExtendedMealTime): boolean {
  const { currentWeekday, currentHour, currentMinute, estNowTimestamp } = getESTNow();

  const isSpecificDate =
    meal.type === "LIMITED_TIME" ||
    (meal.startsAt && (meal.startsAt.includes("T") || meal.startsAt.includes("-"))) ||
    (meal.endsAt && (meal.endsAt.includes("T") || meal.endsAt.includes("-")));

  // 1. LIMITED TIME: Strict EST Wall-Clock Timestamp Comparison
  if (isSpecificDate && meal.startsAt && meal.endsAt) {
    const startTimestamp = parseEstWallClockTimestamp(meal.startsAt);
    const endTimestamp = parseEstWallClockTimestamp(meal.endsAt);

    const active = estNowTimestamp >= startTimestamp && estNowTimestamp <= endTimestamp;
    return active;
  }

  const nowMinutes = currentHour * 60 + currentMinute;

  const isTimeInRange = (startStr?: string | null, endStr?: string | null): boolean => {
    if (!startStr || !endStr || !startStr.includes(":") || !endStr.includes(":")) {
      return false;
    }

    const [startH, startM] = startStr.split(":").map(Number);
    const [endH, endM] = endStr.split(":").map(Number);

    const startTotalMinutes = startH * 60 + startM;
    const endTotalMinutes = endH * 60 + endM;

    if (endTotalMinutes >= startTotalMinutes) {
      return nowMinutes >= startTotalMinutes && nowMinutes <= endTotalMinutes;
    }
    // Crosses midnight (e.g. 22:00 to 02:00)
    return nowMinutes >= startTotalMinutes || nowMinutes <= endTotalMinutes;
  };

  const mealTypeNormalized = (meal.type || "").trim().toUpperCase();

  // 2. WEEK / DAYS: Day-specific schedule
  if ((mealTypeNormalized === "DAYS" || mealTypeNormalized === "WEEK") && Array.isArray(meal.days) && meal.days.length > 0) {
    const todaySchedule = meal.days.find(
      (d) => d.day && d.day.trim().toLowerCase() === currentWeekday.trim().toLowerCase()
    );
    
    if (!todaySchedule) {
      return false;
    }
    
    const active = isTimeInRange(todaySchedule.startsAt, todaySchedule.endsAt);
    return active;
  }

  // 3. REGULAR / Daily Time Range Check
  const active = isTimeInRange(meal.startsAt, meal.endsAt);
  return active;
}

function* fetchMenuItemsSaga(): Generator<any, void, any> {
  if (shouldSkipApiCalls()) {
    const staticItems = (staticMenuData.data as MenuItemApiResponse[])
      .filter((item) => !item.isDeleted && item.isItemVerified)
      .map((item) => ({
        pk_id: item.id,
        restaurant_id: item.restaurantId || "",
        sku_id: item.id,
        name: item.name,
        category: item.categoryName || "",
        description: item.description || "",
        price: item.price,
        large_price: item.price?.online?.toString() || "0",
        indining_price: item.price?.dining?.toString() || "0",
        indining_large_price: item.price?.dining?.toString() || "0",
        image: item.imageUrl || "",
        is_enabled: true,
        is_item_verified: item.isItemVerified,
        product_description: item.description || "",
        level1_category: item.categoryName || "",
        level2_category: "",
        ingredients: [],
        allergens: [],
        food_type: "",
        is_vegan: "false",
        modifiers_list: [],
        party_orders: [],
        inventoryStatus: item.inventoryStatus,
        mealTimes: [],
        MealName: "",
        mealTimeRange: "",
        isMealTimeActive: false,
        isMealTimeExpired: false,
      }));
    yield put(fetchMenuItemsSuccess(staticItems));
    return;
  }

  try {
    const [menuResponse, mealTimesResponse, mappingsResponse] = yield all([
      call(request, { method: "GET", url: ENDPOINTS.MENU_ITEMS_API_PATH, base: ENDPOINTS.API_V2_BASE_URL }),
      call(request, { method: "GET", url: ENDPOINTS.MEAL_TIMES_API_PATH, base: ENDPOINTS.API_V2_BASE_URL }),
      call(request, { method: "GET", url: ENDPOINTS.MEAL_TIMES_ITEMS_API_PATH, base: ENDPOINTS.API_V2_BASE_URL }),
    ]);

    if (!menuResponse.data.success) {
      throw new Error("Failed to fetch menu data from API response");
    }

    const rawMenuItems = menuResponse.data.data as MenuItemApiResponse[];
    const rawMealTimes = (mealTimesResponse?.data?.success ? mealTimesResponse.data.data : []) as ExtendedMealTime[];
    const rawMappings = mappingsResponse?.data?.success ? mappingsResponse.data.data : [];

    const validMenuItems = rawMenuItems.filter((item) => {
      const isNotDeleted = item.isDeleted === false || item.isDeleted === undefined;
      const isVerified = item.isItemVerified === true;
      return isNotDeleted && isVerified;
    });

    const evaluatedMealTimesMap = new Map<string, { isActive: boolean; isExpired: boolean; mealTime: ExtendedMealTime }>();
    const allMealTimesList: MealTimeInfo[] = [];

    const { estNowTimestamp } = getESTNow();

    rawMealTimes.forEach((mealTime) => {
      const apiIsActive = mealTime.isActive !== false;
      const visibleOnWeb = mealTime.settings?.visibleOnWeb !== false;
      const isLimitedTime =
        mealTime.type === "LIMITED_TIME" ||
        (mealTime.startsAt && (mealTime.startsAt.includes("T") || mealTime.startsAt.includes("-")));

      const timeCalculatedActive = isCurrentMealTimeActive(mealTime);
      
      // Strict filter: Must be API active, visible on web, and time window active
      const isActive = apiIsActive && visibleOnWeb && timeCalculatedActive;
      
      let isExpired = false;
      if (isLimitedTime && mealTime.endsAt) {
        const endTimestamp = parseEstWallClockTimestamp(mealTime.endsAt);
        isExpired = endTimestamp < estNowTimestamp;
      }

      evaluatedMealTimesMap.set(mealTime.id, { isActive, isExpired, mealTime });

      if (!allMealTimesList.some((m) => m.mealTimeId === mealTime.id)) {
        allMealTimesList.push({
          mappingId: "",
          mealTimeId: mealTime.id,
          mealTimeName: mealTime.name,
          mealType: mealTime.type || "standard",
          description: mealTime.description || "",
          startsAt: mealTime.startsAt || "",
          endsAt: mealTime.endsAt || "",
          days: mealTime.days || [],
          isActive,
          isExpired,
          price: null,
        });
      }
    });

    const itemMealTimesMap = new Map<string, MealTimeInfo[]>();
    rawMappings.forEach((mapping: any) => {
      const cachedMeal = evaluatedMealTimesMap.get(mapping.mealTimeId);
      if (!cachedMeal) return;

      const { mealTime, isActive, isExpired } = cachedMeal;
      const existingList = itemMealTimesMap.get(mapping.itemId) || [];
      const alreadyExists = existingList.some((s) => s.mealTimeId === mealTime.id);
      if (alreadyExists) return;

      const mealTimeInfo: MealTimeInfo = {
        mappingId: mapping.id || `${mapping.itemId}-${mealTime.id}`,
        mealTimeId: mealTime.id,
        mealTimeName: mealTime.name,
        mealType: mealTime.type || "standard",
        description: mealTime.description || "",
        startsAt: mealTime.startsAt || "",
        endsAt: mealTime.endsAt || "",
        days: mealTime.days || [],
        isActive,
        isExpired,
        price: mapping.price ?? null,
      };

      existingList.push(mealTimeInfo);
      itemMealTimesMap.set(mapping.itemId, existingList);
    });

    const transformedItems: MenuItem[] = validMenuItems.map((item) => {
      const itemSchedules = itemMealTimesMap.get(item.id) || [];
      
      // Target schedule must be active and not expired. If none, targetSchedule is null.
      const targetSchedule = itemSchedules.find((s) => s.isActive && !s.isExpired) || null;

      let onlinePrice = typeof item.price === "object" ? item.price.online : Number(item.price) || 0;
      let diningPrice = typeof item.price === "object" ? item.price.dining : Number(item.indining_price) || 0;

      const firstScheduleWithCustomPrice = itemSchedules.find((s) => s.price !== null && s.price !== undefined);
      const priceSource = firstScheduleWithCustomPrice || targetSchedule;

      if (priceSource && priceSource.price !== null && priceSource.price !== undefined) {
        onlinePrice = priceSource.price;
        diningPrice = priceSource.price;
      }

      const fullMealObj = rawMealTimes.find((m) => m.id === targetSchedule?.mealTimeId);

      let rangeStartsAt = targetSchedule?.startsAt || "";
      let rangeEndsAt = targetSchedule?.endsAt || "";

      if ((fullMealObj?.type === "DAYS" || fullMealObj?.type === "WEEK") && Array.isArray(fullMealObj.days)) {
        const { currentWeekday } = getESTNow();
        const currentDaySched = fullMealObj.days.find((d) => d.day.toLowerCase() === currentWeekday);
        if (currentDaySched) {
          rangeStartsAt = currentDaySched.startsAt;
          rangeEndsAt = currentDaySched.endsAt;
        }
      }

      const formattedRange = targetSchedule
        ? formatDateTimeRange(rangeStartsAt, rangeEndsAt, fullMealObj?.type, fullMealObj?.days)
        : "";

      return {
        pk_id: item.id,
        restaurant_id: item.restaurantId || "",
        sku_id: item.id,
        name: item.name,
        category: targetSchedule ? targetSchedule.mealTimeName : item.categoryName || "",
        description: item.description || "",
        price: {
          dining: diningPrice,
          online: onlinePrice,
        },
        large_price: onlinePrice.toString(),
        indining_price: diningPrice.toString(),
        indining_large_price: diningPrice.toString(),
        image: item.imageUrl || "",
        is_enabled: true,
        is_item_verified: item.isItemVerified,
        product_description: item.description || "",
        level1_category: item.categoryName || "",
        level2_category: "",
        ingredients: item.ingredients || [],
        allergens: item.allergens || [],
        food_type: item.food_type || "",
        is_vegan: item.is_vegan || "false",
        modifiers_list: item.modifiers_list || [],
        party_orders: item.party_orders || [],
        inventoryStatus: item.inventoryStatus,
        mealTimes: itemSchedules,

        MealName: targetSchedule ? targetSchedule.mealTimeName : "",
        mealTimeRange: formattedRange,
        isMealTimeActive: targetSchedule ? targetSchedule.isActive : false,
        isMealTimeExpired: targetSchedule ? targetSchedule.isExpired : false,
      };
    });

    yield put(fetchMenuItemsSuccess(transformedItems));
    yield put(setMealTimes(allMealTimesList));
  } catch (error) {
    const message = error instanceof Error ? error.message : "Unknown error occurred during menu fetching";
    yield put(fetchMenuItemsFailure(message));
  }
}

export function* watchMenuItemsSaga() {
  yield takeEvery(fetchMenuItemsRequest.type, fetchMenuItemsSaga);
}

// ==========================
// DATE / TIME UTILITY HELPERS (EST STRICT)
// ==========================

// Format "HH:mm" or "HH:mm:ss" string into 12-hour AM/PM time
export const formatTime12Hour = (input: string): string => {
  if (!input || typeof input !== "string" || !input.includes(":")) return input;
  
  const [hStr, mStr] = input.split(":");
  let hours = parseInt(hStr, 10);
  if (isNaN(hours)) return input;

  const minutes = (mStr || "00").padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  
  hours = hours % 12;
  hours = hours ? hours : 12;
  
  return `${hours}:${minutes} ${ampm}`;
};

// Reusable helper function to format date/time ranges for the UI badge supporting REGULAR, LIMITED_TIME, and DAYS strictly in EST components
export function formatDateTimeRange(startsAt: string, endsAt: string, type?: string, days?: (MealDaySchedule | string)[]): string {
  try {
    const isSpecificDate = type === "LIMITED_TIME" || startsAt.includes("T") || startsAt.includes("-");
    
    // 1. Handle LIMITED_TIME (Date || Time range) strictly parsed as EST components
    if (isSpecificDate) {
      const [startDatePart, startTimePart = "00:00:00"] = startsAt.split("T");
      const [startYear, startMonth, startDay] = startDatePart.split("-");
      const [startH, startM] = startTimePart.split(":");

      const [endDatePart, endTimePart = "00:00:00"] = endsAt.split("T");
      const [endYear, endMonth, endDay] = endDatePart.split("-");
      const [endH, endM] = endTimePart.split(":");

      const startDateStr = `${startDay}/${startMonth}/${startYear}`;
      const endDateStr = `${endDay}/${endMonth}/${endYear}`;

      const startTimeStr = formatTime12Hour(`${startH}:${startM}`);
      const endTimeStr = formatTime12Hour(`${endH}:${endM}`);

      const dateDisplay = startDateStr === endDateStr 
        ? startDateStr 
        : `${startDateStr} – ${endDateStr}`;

      return `${dateDisplay}||${startTimeStr} – ${endTimeStr}`;
    }

    const timeWindow = `${formatTime12Hour(startsAt)} – ${formatTime12Hour(endsAt)}`;

    // 2. Handle DAYS (Specific days of the week || Time range)
    if (type === "DAYS" && Array.isArray(days) && days.length > 0) {
      const formattedDays = days
        .map((d) => {
          const dayName = typeof d === "string" ? d : d.day;
          return dayName.charAt(0).toUpperCase() + dayName.slice(1).toLowerCase();
        })
        .join(", ");
      return `${formattedDays}||${timeWindow}`;
    }

    // 3. Handle REGULAR (Daily Time range)
    return timeWindow;
  } catch (e) {
    return `${startsAt} – ${endsAt}`;
  }
}